import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { useFMSWebsocket } from "Utils/webSocketUtils";
import { getCurrentSemanticMapId } from "Slices/appState";
import {
  VEHICLE_LIST_MISSIONS_WS,
  VEHICLE_STATE_WS,
  WS_SPINNER_LOGS,
} from "Utils/constants";
import { setVehiclesStateData, updateVehicle } from "Slices/vehiclesState";
import { WebsocketLoaderContext } from "Pages/AuthorizedApp/WebsocketLoaderController";
import { FMSLogger } from "FMSLogger";
import {
  fetchGetActiveMissions,
  getFleetIdsRelatesToVehiclePage,
  setVehiclesPageVehicles,
  updateVehiclesPageMissions,
} from "Slices/vehiclesPage";
import { getVehicles } from "Slices/vehicles";

const wsSpinnerLogs = FMSLogger.byPrefix(`${WS_SPINNER_LOGS}:Intersections`);

const UPDATING_FIELDS = [
  "t_long",
  "t_lat",
  "t_drive_mode",
  "t_web_status",
  "t_virtual_bumper_status",
  "t_vehicle_status",
  "last_contact",
];

export const IntersectionsController = memo(() => {
  const semanticMapId = useSelector(getCurrentSemanticMapId);

  const setIsWSConnecting = useContext(WebsocketLoaderContext);

  useLayoutEffect(
    () => () => {
      wsSpinnerLogs.debug("unmount component spinner off");
      setIsWSConnecting?.(false);
    },
    [setIsWSConnecting]
  );

  const [isVehiclesWSConnected, setIsVehiclesWSConnected] = useState(false);
  const [isMissionWSConnected, setIsMissionWSConnected] = useState(false);

  useEffect(() => {
    if (isVehiclesWSConnected && isMissionWSConnected) {
      wsSpinnerLogs.debug(
        "isVehiclesWSConnected and isMissionWSConnected spinner off"
      );
      setIsWSConnecting?.(false);
    } else {
      wsSpinnerLogs.debug(
        "no isVehiclesWSConnected or no isMissionWSConnected spinner on"
      );
      setIsWSConnecting?.(true);
    }
  }, [isVehiclesWSConnected, isMissionWSConnected, setIsWSConnecting]);

  useEffect(() => {
    // If semanticMapId is null, consider the vehicle websocket "connected"
    // since there's nothing to subscribe to
    if (semanticMapId === null) {
      setIsVehiclesWSConnected(true);
    } else {
      setIsVehiclesWSConnected(false);
    }
  }, [semanticMapId]);

  const dispatch = useDispatch();

  const handleVehicleStateCreateMessage = (data: VehicleState[]) => {
    wsSpinnerLogs.debug("create message spinner off");
    setIsVehiclesWSConnected(true);
    dispatch(setVehiclesStateData(data));
  };

  const handleVehicleStateUpdateMessage = (data: VehicleState) => {
    dispatch(updateVehicle(data));
  };

  useFMSWebsocket({
    url: "/vehicles/state/",
    subscriptionData: semanticMapId,
    loggerFlag: VEHICLE_STATE_WS,
    getDataForSubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
      fields: UPDATING_FIELDS,
    }),
    getDataForUnsubscribe: (subscriptionId) => ({
      semantic_map_ids: [subscriptionId],
    }),
    handleInitialMessage: handleVehicleStateCreateMessage,
    handleMessage: handleVehicleStateUpdateMessage,
  });

  const vehicles = useSelector(getVehicles);

  useEffect(() => {
    dispatch(setVehiclesPageVehicles(vehicles));
  }, [vehicles, dispatch]);

  const fleetIdsRelatesToVehiclePage = useSelector(
    getFleetIdsRelatesToVehiclePage
  );

  // Stable reference for fleet IDs - only updates when actual IDs change
  const prevFleetIdsRef = useRef<string[] | null>(null);
  const [currentFleetIds, setCurrentFleetIds] = useState<string[] | null>(null);

  useEffect(() => {
    const current = fleetIdsRelatesToVehiclePage;
    const prev = prevFleetIdsRef.current;

    // Check if arrays have same content (deep equality)
    const hasChanged =
      !prev ||
      !current ||
      prev.length !== current.length ||
      !prev.every((id, idx) => {
        const sortedCurrent = [...current].sort();
        const sortedPrev = [...prev].sort();
        return sortedPrev[idx] === sortedCurrent[idx];
      });

    if (hasChanged) {
      prevFleetIdsRef.current = current;
      setCurrentFleetIds(current);
      // If currentFleetIds is null, consider the mission websocket "connected"
      // since there's nothing to subscribe to
      if (current === null) {
        setIsMissionWSConnected(true);
      } else {
        setIsMissionWSConnected(false);
      }
    }
  }, [fleetIdsRelatesToVehiclePage]);

  useEffect(() => {
    if (!currentFleetIds) return;

    dispatch(fetchGetActiveMissions(currentFleetIds));
  }, [currentFleetIds, dispatch]);

  useFMSWebsocket({
    url: "/fleets/mission_queue/",
    subscriptionData: currentFleetIds,
    loggerFlag: VEHICLE_LIST_MISSIONS_WS,
    getDataForSubscribe: (subscriptionData) => ({
      fleet_ids: subscriptionData,
    }),
    getDataForUnsubscribe: (subscriptionData) => ({
      fleet_ids: subscriptionData,
    }),
    handleInitialMessage: () => {
      setIsMissionWSConnected(true);
    },
    handleMessage: (data) => {
      const shouldIgnoreUpdate = data?.state === "STATE_RUNNING";
      if (shouldIgnoreUpdate) return;

      dispatch(updateVehiclesPageMissions(data));
    },
  });

  return null;
});
